import React, { useState } from 'react';
import { CornerDownRight, ExternalLink } from 'lucide-react';
import { flows } from '../../data/market/states';
import { glossary, SUPPORT_TEMPLATE } from '../../data/market/topicsHelp';
import { CopyTextButton } from '../world/CopyTextButton';
import { DISCORD_URL } from '../../data/horizon';
import type { MarketWidget } from '../../types/market';
export function MarketWidgetView({
  widget
}: {
  widget: MarketWidget;
}) {
  if (widget === 'states') return <StateExplorer data-id="element-1076" data-mp="3a8jki5myrhw3" />;
  if (widget === 'glossary') return <Glossary data-id="element-1077" data-mp="35arwvntcqhxp" />;
  if (widget === 'support') return <SupportTemplate data-id="element-1078" data-mp="359yu0xxikcku" />;
  return null;
}

/** Explorador visual de estados. Las ramas alternativas se muestran aparte del recorrido principal. */
export function StateExplorer() {
  const [flowId, setFlowId] = useState(flows[0].id);
  const flow = flows.find(f => f.id === flowId) ?? flows[0];
  const [stateId, setStateId] = useState(flow.states[0].id);
  const current = flow.states.find(s => s.id === stateId) ?? flow.states[0];
  const main = flow.states.filter(s => !s.branch);
  const branches = flow.states.filter(s => s.branch);
  const pick = (id: string) => {
    setFlowId(id);
    setStateId(flows.find(f => f.id === id)!.states[0].id);
  };
  const stateBtn = (s: (typeof flow.states)[number]) => <button key={s.id} type="button" aria-pressed={s.id === current.id} onClick={() => setStateId(s.id)} className={`rounded-xl border px-3 py-2 text-left text-sm transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37] ${s.id === current.id ? 'border-[#D4AF37]/60 bg-[#D4AF37]/10 text-white' : 'border-white/10 text-gray-300 hover:border-white/25'}`} data-id="element-1079" data-mp="9bwaree9oop2">
      {s.label}
    </button>;
  return <section id="explorador-estados" aria-labelledby="states-t" className="scroll-mt-32 print:hidden" data-id="element-1080" data-mp="s48cq010dnyo">
      <h2 id="states-t" className="font-cinzel text-lg font-bold text-white md:text-xl" data-id="element-1081" data-mp="wznsixikttxq">
        Explorador de estados
      </h2>
      <div role="group" aria-label="Tipo de operación" className="mt-3 flex flex-wrap gap-2" data-id="element-1082" data-mp="2y2wyhxn1auvc">
        {flows.map(f => <button key={f.id} type="button" aria-pressed={f.id === flow.id} onClick={() => pick(f.id)} className={`h-10 rounded-full px-4 text-sm font-semibold transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37] ${f.id === flow.id ? 'bg-[#D4AF37] text-[#050b14]' : 'border border-white/15 text-gray-300 hover:text-white'}`} data-id="element-1083" data-mp="2t5wppeysve0j">
            {f.name}
          </button>)}
      </div>
      <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_320px]" data-id="element-1084" data-mp="3pucl3rolchlk">
        <div data-id="element-1085" data-mp="3lt12i644wwmv">
          <p className="mb-2 text-xs font-semibold text-gray-500" data-id="element-1086" data-mp="3lulxfwud2hl9">Recorrido principal</p>
          <ol className="flex flex-wrap items-center gap-2" data-id="element-1087" data-mp="1vhwgevbg1bit">
            {main.map((s, i) => <li key={s.id} className="flex items-center gap-2" data-id="element-1088" data-mp="1rgb7tm5xzjef" data-mpi={i}>
                {stateBtn(s)}
                {i < main.length - 1 && <span className="text-gray-600" aria-hidden="true" data-id="element-1089" data-mp="1hbutwyc0wcd9">→</span>}
              </li>)}
          </ol>
          {branches.length > 0 && <>
              <p className="mb-2 mt-4 flex items-center gap-1.5 text-xs font-semibold text-gray-500" data-id="element-1090" data-mp="17kgn05hqsaa3">
                <CornerDownRight className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1091" data-mp="1cjkvsv0r85zw" />
                Ramas alternativas (no son pasos obligatorios)
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-1092" data-mp="12mf5vq3vml19">{branches.map(stateBtn)}</div>
            </>}
        </div>
        <div aria-live="polite" className="rounded-2xl border border-white/10 bg-[#0A1628] p-4" data-id="element-1093" data-mp="on70ugl18ip5">
          <p className="text-xs text-gray-500" data-id="element-1094" data-mp="tl8lv720qs39">{flow.name}</p>
          <p className="mt-1 font-semibold text-white" data-id="element-1095" data-mp="tnut15kzz5ky">{current.label}</p>
          <p className="mt-2 inline-block rounded-full bg-white/5 px-2.5 py-1 text-xs font-semibold text-[#D4AF37]" data-id="element-1096" data-mp="2j180h3oep9qa">{current.effect}</p>
          <p className="mt-3 text-sm leading-relaxed text-gray-300" data-id="element-1097" data-mp="2nxf58t52gf6n">{current.text}</p>
        </div>
      </div>
    </section>;
}
export function Glossary() {
  return <section id="terminos" aria-label="Glosario" className="scroll-mt-32" data-id="element-1098" data-mp="iz8gh3mined1">
      <dl className="grid gap-x-8 sm:grid-cols-2" data-id="element-1099" data-mp="dyb1t10pt2ga">
        {glossary.map(g => <div key={g.term} className="border-b border-white/10 py-3" data-id="element-1100" data-mp="9yio69wgs642">
            <dt className="font-semibold text-white" data-id="element-1101" data-mp="4ynxo7glpijj">{g.term}</dt>
            <dd className="mt-1 text-sm leading-relaxed text-gray-300" data-id="element-1102" data-mp="4zyzw2ueei8j">{g.text}</dd>
          </div>)}
      </dl>
    </section>;
}

/** Plantilla vacía para pedir soporte. Solo se copia: no se envía ni se publica. */
export function SupportTemplate() {
  return <section id="datos-soporte" aria-labelledby="support-t" className="scroll-mt-32 rounded-2xl border border-white/10 bg-[#0A1628] p-4 md:p-5" data-id="element-1103" data-mp="3mg739lhcdgv7">
      <h2 id="support-t" className="font-cinzel text-lg font-bold text-white" data-id="element-1104" data-mp="3ifdhaxngmc4y">
        Datos para soporte
      </h2>
      <p className="mt-1 text-sm text-gray-400" data-id="element-1105" data-mp="1swp0mgxzoujh">Copia la plantilla, complétala tú y pégala en el canal de atención. La guía no la envía ni guarda nada.</p>
      <pre className="mt-3 whitespace-pre-wrap rounded-xl border border-white/10 bg-[#050b14] p-3 text-xs leading-relaxed text-gray-300" data-id="element-1106" data-mp="1xtymxnq30q33">{SUPPORT_TEMPLATE}</pre>
      <div className="mt-3 flex flex-wrap items-center gap-3 print:hidden" data-id="element-1107" data-mp="26wpxzmf1pju7">
        <CopyTextButton text={SUPPORT_TEMPLATE} label="Copiar datos para soporte" data-id="element-1108" data-mp="1a5xleyrbm9x6" />
        <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer" className="inline-flex h-11 items-center gap-2 rounded-full border border-white/15 px-4 text-sm font-semibold text-gray-200 transition-colors duration-150 hover:border-white/30 hover:text-white" data-id="element-1109" data-mp="1a44a365rj1iu">
          Discord del servidor
          <ExternalLink className="h-4 w-4" aria-hidden="true" data-id="element-1110" data-mp="1nz6pql0ew58u" />
          <span className="sr-only" data-id="element-1111" data-mp="qh6jqqe3tt7t">(se abre en otra pestaña)</span>
        </a>
      </div>
    </section>;
}